import React, { createContext, useState, ReactNode, useEffect, useCallback } from "react";
import { ActionItem, ChannelState, ChannelSubstate, ChannelType, SKChannel, Tier } from "../models/SKChannel";
import {
  createChannel,
  createChannelEdits,
  deleteChannel,
  getMyChannel,
  getChannelEdits,
  getUserCreatedChannel,
  updateChannel,
  updateChannelEdits,
  updateChannelStatus,
  updateChannelHistory,
  deleteChannelEdits,
  updateChannelEditStatus,
} from "../api/channels/channelServices";
import { useAuth } from "./AuthContext";
import { ContentAgeRestriction } from "../api/enums";

export interface EditChannelContextType {
  channelEditData: SKChannel;
  updateChannelEditData: React.Dispatch<React.SetStateAction<SKChannel>>;
  loading: boolean;
  setChannelId: (channelId: string) => void;
  updateEditDataOnServer?: () => void;
  updateStatus?: (contentId: string, actionItem: ActionItem) => void;
  originalState?: { status: ChannelState; subStatus: ChannelSubstate };
}

const signupInitialState: EditChannelContextType = {
  setChannelId: () => {},
  channelEditData: {
    name: "",
    description: "",
    profileImageUrl: "",
    backgroundImageUrl: "",
    language: "",
    secondLanguage: "",
    country: "",
    externalLinks: null,
    topic: null,
    certifyFirst: false,
    certifySecond: false,
    autoPublish: false,
    status: ChannelState.DRAFT,
    subStatus: ChannelSubstate.NONE,
  },
  updateChannelEditData: () => {},
  loading: false,
};

export const EditChannelContext = createContext<EditChannelContextType>(signupInitialState);

interface EditChannelProviderProps {
  children: ReactNode;
}

const EditChannelProvider = ({ children }: EditChannelProviderProps) => {
  const [channelEditData, updateChannelEditData] = useState<SKChannel>({
    name: "",
    description: "",
    profileImageUrl: "",
    backgroundImageUrl: "",
    language: "None",
    secondLanguage: "None",
    country: "None",
    externalLinks: null,
    topic: null,
    certifyFirst: false,
    certifySecond: false,
    autoPublish: false,
    status: ChannelState.DRAFT,
    subStatus: ChannelSubstate.NONE,
    channelType: ChannelType.creator,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [originalState, setOriginalState] = useState<{ status: ChannelState; subStatus: ChannelSubstate } | undefined>(undefined);
  const [channelId, setChannelId] = useState<string | null>(null);
  const authContext = useAuth();

  const updateEditDataOnServer = useCallback(async () => {
    setLoading(true);
    if (channelEditData) {
      if (channelEditData.id) {
        await updateChannelEdits(channelEditData.id!, channelEditData);
      } else {
        if (authContext && authContext.myChannel && authContext.myChannel.id) {
          await createChannelEdits(authContext.myChannel.id, channelEditData);
          updateChannelEditData({ ...channelEditData, id: authContext.myChannel.id });
        }
      }
    }
    setLoading(false);
  }, [channelEditData]);

  const updateStatus = async (contentId: string, actionItem: ActionItem) => {
    var data: any = {};
    setLoading(true);
    switch (actionItem) {
      case ActionItem.submit_for_review:
        // await updateChannelStatus(contentId, ChannelState.IN_REVIEW, ChannelSubstate.NONE);
        data = { status: ChannelState.IN_REVIEW, subStatus: ChannelSubstate.NONE };
        await updateChannel(contentId, { ...channelEditData, ...data });
        await updateChannelHistory(contentId, "Submitted for Review");

        break;
      case ActionItem.edit_submit:
        data = { status: ChannelState.ACTIVE, subStatus: ChannelSubstate.EDIT_IN_REVIEW };
        await updateChannelEdits(contentId, { ...channelEditData, ...data });
        await updateChannelEditStatus(contentId, ChannelState.ACTIVE, ChannelSubstate.EDIT_IN_REVIEW);
        setOriginalState({ status: ChannelState.ACTIVE, subStatus: ChannelSubstate.EDIT_IN_REVIEW });
        break;
      case ActionItem.withdraw:
        await updateChannelStatus(contentId, ChannelState.DRAFT, ChannelSubstate.WITHDRAWN);
        data = { status: ChannelState.DRAFT, subStatus: ChannelSubstate.WITHDRAWN };
        (channelEditData.subStatus === ChannelSubstate.EDIT_IN_DRAFT ||
          channelEditData.subStatus === ChannelSubstate.EDIT_REJECTED ||
          channelEditData.subStatus === ChannelSubstate.EDIT_IN_REVIEW) &&
          (await deleteChannelEdits(contentId));
        break;
      case ActionItem.delete:
        deleteChannel(contentId);
        break;
      default:
    }
    updateChannelEditData(prev => ({ ...prev, ...data }));
    setLoading(false);
  };

  useEffect(() => {
    const getEditChannel = async () => {
      setLoading(true);
      if (authContext && authContext.myChannel && authContext.myChannel.id) {
        try {
          const { status, subStatus } = await getMyChannel();
          const channelnew = await getChannelEdits(authContext.myChannel.id);
          setOriginalState({ status, subStatus });
          updateChannelEditData(prev => ({ ...prev, ...channelnew }));
        } catch (error) {
          const channelnew = await getMyChannel();
          const { id, subStatus, ...rest } = channelnew;
          const channelEdits = await createChannelEdits(authContext.myChannel.id, { ...rest, subStatus: ChannelSubstate.EDIT_IN_DRAFT });
          setOriginalState({ status: channelEdits.status, subStatus: channelEdits.subStatus });
          updateChannelEditData({ ...channelEdits, id });
        } finally {
          setLoading(false);
        }
      }
    };
    getEditChannel();
  }, [authContext]);

  return (
    <EditChannelContext.Provider
      value={{ setChannelId, channelEditData, updateEditDataOnServer, updateStatus, originalState, updateChannelEditData, loading }}
    >
      {children}
    </EditChannelContext.Provider>
  );
};
export default EditChannelProvider;
